import React from 'react';
import styled from 'styled-components';
import { useGlobalTheme } from '@hooks/application/useColorTheme';
import { Body5, Body7, Container } from '@ui';
import { GhostButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { MAX_PAGE_SIZE, isRunTerminal } from '@services/fmdc';
import { Disclosure } from '@features/feedView/Disclosure';
import { FilterGroup, GroupLabel, SegmentedControl } from '@features/scoreSpec/SegmentedControl';
import { GateNotice } from './GateNotice';
import { Pager } from './Pager';
import { RunsTable } from './RunsTable';
import { UploadPanel } from './UploadPanel';
import { RUN_STATUS_FILTERS, runsUrl } from './fmdcUrl';
import { formatCount, formatUpdatedAgo } from './format';
import { FilterRow, SearchInput } from './styles';
import { useNowTick } from './useFmdcPoll';
import { isRunningBucket, useRuns } from './useRuns';
import { useUrlFilters } from '@hooks/application/useUrlFilters';
import { useDebouncedSearch } from './useDebouncedSearch';

// The status buckets on the filter bar, with the dot each carries (RUN_STATUS_TOKENS).
export const STATUS_SEGMENTS = [
  { key: RUN_STATUS_FILTERS.ALL, label: 'All', token: 'text' },
  { key: RUN_STATUS_FILTERS.RUNNING, label: 'Running', token: 'primaryCtaButton' },
  { key: RUN_STATUS_FILTERS.REVIEW, label: 'Needs review', token: 'warning' },
  { key: RUN_STATUS_FILTERS.LOADABLE, label: 'Loadable', token: 'positive' },
  { key: RUN_STATUS_FILTERS.ERROR, label: 'Error', token: 'negative' },
];

// GhostButton: the disclosure's trigger is pressable but must not read as a CTA. Chrome reset and
// the focus ring come with it; the summary's type is a Body5, and the colour here is what the
// chevron's currentColor reads.
const DisclosureTrigger = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.themeColors.text};
`;

const UploadPanelSlot = styled.div`
  margin-top: 12px;
`;

// Filters on the left, Refresh on the right. FilterRow lines its controls up by their BOTTOM edge
// (the labels sit above 40px controls), so this row aligns to flex-end too; centring the Refresh
// group would float it against the labels' mid-line, ~9px above the controls.
const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px 24px;
  margin-top: 24px;
`;

const isFiltering = (filters) => filters.status !== RUN_STATUS_FILTERS.ALL || !!filters.issuer;

export const RunsView = () => {
  const theme = useGlobalTheme();
  const { filters, setFilters } = useUrlFilters(runsUrl);
  const { runs, total, isLoading, error, lastLoadedAt, reload, truncated } = useRuns(filters);
  const now = useNowTick(1000, !!runs);

  // The issuer box is bound to local state and debounced into the URL, so the list is not
  // re-fetched on every keystroke (the ScoreBackfillForm pattern, with its isCanceled guard).
  const [issuerDraft, setIssuerDraft] = useDebouncedSearch(filters.issuer, (issuer) => setFilters({ issuer }));

  // Open until the reviewer closes it, unless the list already has runs — then uploading is the
  // exception on this screen and the disclosure starts folded. `null` means untouched.
  const [uploadToggled, setUploadToggled] = React.useState(null);
  const isUploadOpen = uploadToggled !== null ? uploadToggled : !runs || runs.length === 0;

  if (isLoading) {
    return <Body5 isLowContrast>Loading runs…</Body5>;
  }

  const visibleRuns = isRunningBucket(filters.status) ? runs.filter((run) => !isRunTerminal(run)) : runs;
  const updatedLabel = formatUpdatedAgo(lastLoadedAt, now);
  const storeIsEmpty = runs.length === 0 && !isFiltering(filters);

  const clearFilters = () => {
    setIssuerDraft('');
    setFilters({ status: RUN_STATUS_FILTERS.ALL, issuer: '', offset: 0 });
  };

  return (
    <div>
      <Disclosure
        isOpen={isUploadOpen}
        onToggle={() => setUploadToggled(!isUploadOpen)}
        summary={<Body5 bold>Upload a model</Body5>}
        triggerAs={DisclosureTrigger}
        panelAs={UploadPanelSlot}
      >
        <UploadPanel
          onUploaded={() => {
            // The first upload turns an empty store into a list, which would fold the disclosure
            // and unmount the notice the user is reading. An upload is a toggle-open.
            setUploadToggled(true);
            reload();
          }}
        />
      </Disclosure>

      <Toolbar>
        <FilterRow>
          <SegmentedControl
            label="Status"
            options={STATUS_SEGMENTS}
            active={filters.status}
            onSelect={(status) => setFilters({ status })}
            withDots
          />
          <FilterGroup>
            <GroupLabel as="label" htmlFor="fmdc-runs-issuer">
              Issuer
            </GroupLabel>
            <SearchInput
              id="fmdc-runs-issuer"
              aria-label="Filter by issuer id"
              placeholder="Issuer id…"
              value={issuerDraft}
              onChange={(e) => setIssuerDraft(e.target.value)}
            />
          </FilterGroup>
        </FilterRow>
        <Container row verticallyCenter>
          {updatedLabel && (
            <Container right={12}>
              <Body7 isLowContrast>{updatedLabel}</Body7>
            </Container>
          )}
          <TransparentFlatButton onClick={reload}>Refresh</TransparentFlatButton>
        </Container>
      </Toolbar>

      {error && (
        <Container top={12}>
          <Body5 color={theme.themeColors.error}>{error}</Body5>
          {runs.length === 0 && (
            <Container top={12}>
              <SkeletonButton onClick={reload}>Retry</SkeletonButton>
            </Container>
          )}
        </Container>
      )}

      {/* The centred GateNotice is for the first-ever empty store only; a filter that empties the
          list is an in-table state, with the same Clear filters the count line carries. */}
      {storeIsEmpty ? (
        <Container top={16}>
          <GateNotice
            compact
            title="No models uploaded yet"
            body="Upload an analyst's Excel model above to start a run."
          />
        </Container>
      ) : visibleRuns.length === 0 ? (
        <>
          <Container top={16} bottom={8}>
            <Body5 isLowContrast>No runs match these filters.</Body5>
          </Container>
          <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>
        </>
      ) : (
        <>
          <Container top={12} row verticallyCenter gap={12}>
            <Body7 isLowContrast role="status">
              {isRunningBucket(filters.status)
                ? `${formatCount(visibleRuns.length)} running${
                    truncated ? ` among the newest ${formatCount(MAX_PAGE_SIZE)} runs` : ''
                  }`
                : `${formatCount(total)} ${total === 1 ? 'run' : 'runs'}`}
            </Body7>
            {isFiltering(filters) && (
              <TransparentFlatButton onClick={clearFilters}>Clear filters</TransparentFlatButton>
            )}
          </Container>
          <RunsTable runs={visibleRuns} now={now} onReload={reload} />
        </>
      )}

      {!storeIsEmpty && !isRunningBucket(filters.status) && (
        <Pager
          offset={filters.offset}
          total={total}
          count={runs.length}
          onChange={(offset) => setFilters({ offset })}
        />
      )}
    </div>
  );
};
